import { Link, useRouteError, isRouteErrorResponse } from "react-router";
import { motion } from "framer-motion";
import { ArrowLeft, Home, Search } from "lucide-react";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import Footer from "@/components/ui/Footer";
import Blob from "@/components/ui/Blob";
import StitchDots from "@/components/ui/StitchDots";
import ThreadDecoration from "@/components/ui/ThreadDecoration";

type Props = {
  title?: string;
  message?: string;
  status?: number | null;
};

export function NotFound({ title, message, status }: Props = {}) {
  const theme = usePalette();
  const heading = title ?? "Loose thread.";
  const sub =
    message ??
    "We couldn't find that page. Maybe it moved, maybe it never existed, maybe it's hiding in the sewing basket.";
  const code = status ?? 404;

  return (
    <div className="min-h-dvh" style={{ backgroundColor: colors.cream }}>

      <section
        className="relative flex min-h-[80dvh] items-center overflow-hidden pt-24 pb-20"
        style={{ backgroundColor: colors.cream }}
      >
        <Blob className="w-96 h-96 -top-20 -left-10" color={theme.blob1} />
        <Blob className="w-80 h-80 bottom-0 -right-10" color={theme.blob3} />
        <Blob className="w-64 h-64 top-1/3 left-1/2" color={theme.blob2} />
        <StitchDots />

        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-3 text-xs uppercase"
            style={{
              fontFamily: fonts.body,
              fontWeight: 800,
              letterSpacing: "0.2em",
              color: theme.accent,
            }}
          >
            Error {code}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="text-5xl sm:text-6xl lg:text-7xl"
            style={{
              fontFamily: fonts.logo,
              fontWeight: 400,
              color: colors.navy,
              lineHeight: 1.05,
            }}
          >
            A{" "}
            <span style={{ fontFamily: fonts.heading, color: theme.warm }}>
              loose
            </span>{" "}
            thread.
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mx-auto my-6 w-40"
          >
            <ThreadDecoration className="w-full opacity-70" />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mx-auto max-w-xl text-base"
            style={{
              fontFamily: fonts.body,
              fontWeight: 500,
              color: `${colors.navy}b3`,
              letterSpacing: "0.03em",
              lineHeight: 1.7,
            }}
          >
            {sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-3"
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-white transition-all hover:shadow-xl hover:scale-105 active:scale-[0.98]"
              style={{
                backgroundColor: theme.accent,
                fontFamily: fonts.button,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                fontSize: "0.75rem",
              }}
            >
              <Home size={14} />
              Back home
            </Link>
            <button
              type="button"
              onClick={() => window.history.back()}
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 transition-all hover:shadow-md"
              style={{
                backgroundColor: colors.cream,
                color: colors.navy,
                border: `1px solid ${colors.tan}55`,
                fontFamily: fonts.button,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                fontSize: "0.75rem",
              }}
            >
              <ArrowLeft size={14} />
              Go back
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-14"
          >
            <p
              className="mb-4 text-[11px] uppercase"
              style={{
                fontFamily: fonts.body,
                fontWeight: 700,
                letterSpacing: "0.18em",
                color: `${colors.navy}77`,
              }}
            >
              Or try one of these
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {[
                { label: "Guides", href: "/guides" },
                { label: "Shop", href: "/shop" },
                { label: "Retreats", href: "/retreats" },
                { label: "Favorites", href: "/favorites" },
                { label: "About", href: "/about" },
              ].map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 transition-all hover:-translate-y-0.5 hover:shadow-md"
                  style={{
                    backgroundColor: `${theme.accentSoft}`,
                    color: colors.navy,
                    fontFamily: fonts.body,
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    fontSize: "0.75rem",
                    border: `1px solid ${colors.tan}1f`,
                  }}
                >
                  <Search size={12} />
                  {link.label}
                </Link>
              ))}
            </div>
          </motion.div>

          <p className="sr-only">Page not found ({code}). {heading}</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/**
 * Wrapper for React Router's `errorElement`. Distinguishes "no route matched"
 * (clean 404) from runtime errors thrown inside a route component.
 */
export function RouteError() {
  const error = useRouteError();
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) return <NotFound />;
    return (
      <NotFound
        title="Something snagged."
        message={error.statusText || "The page hit an unexpected error. Try again in a moment."}
        status={error.status}
      />
    );
  }
  return (
    <NotFound
      title="Something snagged."
      message="The page hit an unexpected error. Try refreshing — if it sticks around, drop a note via the newsletter footer."
      status={500}
    />
  );
}
